import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { addDaysToLocalDateInputValue } from "@/utils/date";
import {
  DollarSign,
  FileText,
  TrendingUp,
  Clock,
  Users,
  CreditCard,
  ArrowUp,
  ArrowDown,
  BarChart3,
  PieChart,
  ShoppingCart,
  UserPlus,
  ListChecks,
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const canRecordPayment = ["admin", "finance"].includes(role);
  const parsedReportPermissions =
    typeof user?.report_permissions === "string"
      ? (() => {
          try {
            return JSON.parse(user.report_permissions);
          } catch (_) {
            return null;
          }
        })()
      : user?.report_permissions || null;
  const canViewReports = role === "admin" || Boolean(parsedReportPermissions?.enabled);
  const hasAllowedReports =
    Array.isArray(parsedReportPermissions?.allowed_reports) &&
    parsedReportPermissions.allowed_reports.length > 0;
  const reportsQuickActionEnabled = role === "admin" || (canViewReports && hasAllowedReports);
  const [stats, setStats] = useState(null);
  const [openOrders, setOpenOrders] = useState([]);
  const [funnelSummary, setFunnelSummary] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [duePayments, setDuePayments] = useState([]);
  const [dailyReport, setDailyReport] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [workflowSummary, setWorkflowSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("OMR");

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (user?.role === "sales" || user?.role === "admin") {
      api.dashboard.getOpenOrders().then(setOpenOrders).catch(() => setOpenOrders([]));
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === "management" || user?.role === "admin") {
      api.dashboard.getSalesFunnelSummary().then(setFunnelSummary).catch(() => setFunnelSummary(null));
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === "finance" || user?.role === "admin") {
      api.dashboard.getPendingApprovals().then(setPendingApprovals).catch(() => setPendingApprovals([]));
      api.dashboard.getDuePayments().then(setDuePayments).catch(() => setDuePayments([]));
    }
  }, [user?.role]);

  useEffect(() => {
    api.dashboard.getTasks().then(setTasks).catch(() => setTasks([]));
    api.workflowTickets.summary().then(setWorkflowSummary).catch(() => setWorkflowSummary(null));
    if (["admin", "finance", "management"].includes(user?.role)) {
      const localToday = new Date().toLocaleDateString("en-CA");
      api.dashboard
        .getDailyReport(localToday)
        .then(setDailyReport)
        .catch(() => setDailyReport(null));
    }
  }, [user?.role]);

  const updateTaskStatus = async (taskId, status) => {
    const updated = await api.dashboard.updateTask(taskId, status);
    setTasks((prev) => prev.map((task) => (task.id === taskId ? updated : task)));
  };

  const fetchStats = async () => {
    try {
      const [data, settings] = await Promise.all([
        api.dashboard.getStats(),
        api.settings.getCompany().catch(() => ({ currency: "OMR" })),
      ]);
      setStats(data);
      setCurrency(settings?.currency || "OMR");
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  // Role-based KPI cards
  const getStatCards = () => {
    const role = user?.role;
    if (role === "sales") {
      return [
        {
          title: "Open Orders",
          value: stats?.open_orders_count ?? 0,
          icon: ShoppingCart,
          color: "text-blue-600",
          bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
          borderColor: "border-blue-200",
          image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=300&fit=crop",
        },
        {
          title: "Follow-ups",
          value: stats?.follow_ups_count ?? 0,
          icon: UserPlus,
          color: "text-amber-600",
          bgColor: "bg-gradient-to-br from-amber-50 to-amber-100",
          borderColor: "border-amber-200",
          image: "https://images.unsplash.com/photo-1554224154-26032c5fd44a?w=400&h=300&fit=crop",
        },
        {
          title: "My Revenue",
          value: stats?.my_revenue ?? 0,
          icon: DollarSign,
          color: "text-emerald-600",
          bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
          borderColor: "border-emerald-200",
          format: (val) => `${currency} ${val.toFixed(2)}`,
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
        {
          title: "Pending Approval (mine)",
          value: stats?.my_pending ?? 0,
          icon: Clock,
          color: "text-cyan-600",
          bgColor: "bg-gradient-to-br from-cyan-50 to-cyan-100",
          borderColor: "border-cyan-200",
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
      ];
    }
    if (role === "finance") {
      return [
        {
          title: "Pending Approvals",
          value: stats?.pending_approval ?? 0,
          icon: FileText,
          color: "text-amber-600",
          bgColor: "bg-gradient-to-br from-amber-50 to-amber-100",
          borderColor: "border-amber-200",
          image: "https://images.unsplash.com/photo-1554224154-26032c5fd44a?w=400&h=300&fit=crop",
        },
        {
          title: "Due Payments (30d)",
          value: stats?.due_payments_count ?? 0,
          icon: CreditCard,
          color: "text-red-600",
          bgColor: "bg-gradient-to-br from-red-50 to-red-100",
          borderColor: "border-red-200",
          subtitle: stats?.due_payments_amount != null ? `${currency} ${stats.due_payments_amount.toFixed(2)}` : null,
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
        {
          title: "Total Revenue",
          value: stats?.total_revenue ?? 0,
          icon: DollarSign,
          color: "text-emerald-600",
          bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
          borderColor: "border-emerald-200",
          format: (val) => `${currency} ${val.toFixed(2)}`,
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
        {
          title: "Monthly Profit",
          value: stats?.profit ?? 0,
          icon: TrendingUp,
          color: "text-teal-600",
          bgColor: "bg-gradient-to-br from-teal-50 to-teal-100",
          borderColor: "border-teal-200",
          format: (val) => `${currency} ${val.toFixed(2)}`,
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
      ];
    }
    if (role === "management" || role === "admin") {
      return [
        {
          title: "Total Revenue",
          value: stats?.total_revenue ?? 0,
          icon: DollarSign,
          color: "text-emerald-600",
          bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
          borderColor: "border-emerald-200",
          format: (val) => `${currency} ${val.toFixed(2)}`,
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
        {
          title: "Total Invoices",
          value: stats?.total_invoices ?? 0,
          icon: FileText,
          color: "text-blue-600",
          bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
          borderColor: "border-blue-200",
          image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=300&fit=crop",
        },
        {
          title: "Pending Approval",
          value: stats?.pending_approval ?? 0,
          icon: Clock,
          color: "text-amber-600",
          bgColor: "bg-gradient-to-br from-amber-50 to-amber-100",
          borderColor: "border-amber-200",
          image: "https://images.unsplash.com/photo-1554224154-26032c5fd44a?w=400&h=300&fit=crop",
        },
        {
          title: "Net Profit",
          value: stats?.profit ?? 0,
          icon: TrendingUp,
          color: "text-teal-600",
          bgColor: "bg-gradient-to-br from-teal-50 to-teal-100",
          borderColor: "border-teal-200",
          format: (val) => `${currency} ${val.toFixed(2)}`,
          image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
        },
      ];
    }
    // fallback
    return [
      {
        title: "Total Revenue",
        value: stats?.total_revenue ?? 0,
        icon: DollarSign,
        color: "text-emerald-600",
        bgColor: "bg-gradient-to-br from-emerald-50 to-emerald-100",
        borderColor: "border-emerald-200",
        format: (val) => `${currency} ${val.toFixed(2)}`,
        image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
      },
      {
        title: "Total Invoices",
        value: stats?.total_invoices ?? 0,
        icon: FileText,
        color: "text-blue-600",
        bgColor: "bg-gradient-to-br from-blue-50 to-blue-100",
        borderColor: "border-blue-200",
        image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&h=300&fit=crop",
      },
      {
        title: "Pending Approval",
        value: stats?.pending_approval ?? 0,
        icon: Clock,
        color: "text-amber-600",
        bgColor: "bg-gradient-to-br from-amber-50 to-amber-100",
        borderColor: "border-amber-200",
        image: "https://images.unsplash.com/photo-1554224154-26032c5fd44a?w=400&h=300&fit=crop",
      },
      {
        title: "Net Profit",
        value: stats?.profit ?? 0,
        icon: TrendingUp,
        color: "text-teal-600",
        bgColor: "bg-gradient-to-br from-teal-50 to-teal-100",
        borderColor: "border-teal-200",
        format: (val) => `${currency} ${val.toFixed(2)}`,
        image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop",
      },
    ];
  };

  const statCards = getStatCards();

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Quick Actions — top */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Link
          to="/invoices/new"
          className="group relative overflow-hidden bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] animate-slideUp"
        >
          <div className="absolute inset-0 bg-black opacity-20 group-hover:opacity-10 transition-opacity"></div>
          <div className="relative z-10">
            <FileText className="w-8 h-8 mb-2" />
            <p className="font-semibold">Create Invoice</p>
          </div>
        </Link>
        <Link
          to="/customers"
          className="group relative overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] animate-slideUp"
          style={{ animationDelay: "50ms" }}
        >
          <div className="absolute inset-0 bg-black opacity-20 group-hover:opacity-10 transition-opacity"></div>
          <div className="relative z-10">
            <Users className="w-8 h-8 mb-2" />
            <p className="font-semibold">Add Customer</p>
          </div>
        </Link>
        {canRecordPayment ? (
          <Link
            to="/payments"
            className="group relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] animate-slideUp"
            style={{ animationDelay: "100ms" }}
          >
            <div className="absolute inset-0 bg-black opacity-20 group-hover:opacity-10 transition-opacity"></div>
            <div className="relative z-10">
              <CreditCard className="w-8 h-8 mb-2" />
              <p className="font-semibold">Record Payment</p>
            </div>
          </Link>
        ) : (
          <Link
            to={`/workflow-tickets?source_type=payment&source_id=dashboard&to_role=finance&title=Payment%20collection%20required&description=Please%20record%20or%20verify%20payment%20from%20dashboard.&due_date=${addDaysToLocalDateInputValue(1)}`}
            className="group relative overflow-hidden bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] animate-slideUp"
            style={{ animationDelay: "100ms" }}
          >
            <div className="absolute inset-0 bg-black opacity-20 group-hover:opacity-10 transition-opacity"></div>
            <div className="relative z-10">
              <ListChecks className="w-8 h-8 mb-2" />
              <p className="font-semibold">Generate Payment Ticket</p>
            </div>
          </Link>
        )}
        {reportsQuickActionEnabled && (
          <Link
            to="/reports"
            className="group relative overflow-hidden bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] animate-slideUp"
            style={{ animationDelay: "150ms" }}
          >
            <div className="absolute inset-0 bg-black opacity-20 group-hover:opacity-10 transition-opacity"></div>
            <div className="relative z-10">
              <TrendingUp className="w-8 h-8 mb-2" />
              <p className="font-semibold">View Reports</p>
            </div>
          </Link>
        )}
      </div>

      {/* Stats Grid with Images */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          const numVal = Number(card.value) || 0;
          const displayValue = card.format
            ? card.format(numVal)
            : (card.value != null && typeof card.value === "number" ? card.value : numVal).toLocaleString();
          return (
            <div
              key={index}
              className="group relative overflow-hidden bg-white rounded-2xl border-2 border-stone-200 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:border-cyan-300 animate-slideUp"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Background Image */}
              <div
                className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500 bg-cover bg-center"
                style={{ backgroundImage: `url(${card.image})` }}
              ></div>

              {/* Gradient Overlay */}
              <div
                className={`absolute top-0 right-0 w-32 h-32 ${card.bgColor} rounded-full blur-3xl opacity-50 group-hover:opacity-70 transition-opacity`}
              ></div>

              <div className="relative z-10 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className={`${card.bgColor} p-4 rounded-xl border-2 ${card.borderColor} group-hover:scale-110 transition-transform duration-300 shadow-md`}
                  >
                    <Icon className={`${card.color} w-6 h-6`} />
                  </div>
                  {card.trend && (
                    <div
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                        card.trendUp
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {card.trendUp ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      )}
                      {card.trend}
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-stone-600 mb-2 group-hover:text-stone-800 transition-colors">
                  {card.title}
                </h3>
                <p className="text-3xl font-bold text-gray-900 font-mono mb-2 group-hover:text-cyan-600 transition-colors">
                  {displayValue}
                </p>
                {card.subtitle && (
                  <p className="text-xs text-stone-500 mb-2">{card.subtitle}</p>
                )}
                <div className="h-1 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${card.color.replace(
                      "text-",
                      "bg-"
                    )} rounded-full transition-all duration-1000`}
                    style={{
                      width: `${Math.min((card.value / 10000) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {workflowSummary && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <h2 className="text-base font-semibold text-secondary mb-3 flex items-center gap-2">
            <ListChecks size={18} />
            Workflow Tickets
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-xs text-stone-500">My inbox (open)</p>
              <p className="text-xl font-bold text-amber-700">
                {Number(workflowSummary.my_inbox_open || 0)}
              </p>
            </div>
            <div className="rounded-lg bg-cyan-50 p-3">
              <p className="text-xs text-stone-500">My outbox (open)</p>
              <p className="text-xl font-bold text-cyan-700">
                {Number(workflowSummary.my_outbox_open || 0)}
              </p>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3">
              <p className="text-xs text-stone-500">Done today</p>
              <p className="text-xl font-bold text-emerald-700">
                {Number(workflowSummary.my_done_today || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-4">
            Today's Tasks
          </h2>
          {tasks.length === 0 ? (
            <p className="text-sm text-stone-500">
              No role-wise tasks assigned for today.
            </p>
          ) : (
            <ul className="space-y-2">
              {tasks.map((task) => (
                <li
                  key={task.id}
                  className="flex items-start gap-3 rounded-lg border border-stone-100 p-3"
                >
                  <input
                    type="checkbox"
                    checked={task.status === "completed"}
                    onChange={(e) =>
                      updateTaskStatus(
                        task.id,
                        e.target.checked ? "completed" : "pending",
                      )
                    }
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-stone-800">{task.title}</p>
                    {task.description && (
                      <p className="text-sm text-stone-500">
                        {task.description}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {dailyReport && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-secondary mb-4">
              Daily Report
            </h2>
            {dailyReport?.date && (
              <p className="text-xs text-stone-500 mb-2">
                Date: {new Date(dailyReport.date).toLocaleDateString()}
              </p>
            )}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-emerald-50 p-3">
                <p className="text-xs text-stone-500">Income</p>
                <p className="font-mono font-semibold text-emerald-700">
                  {currency} {Number(dailyReport.sales_summary?.income || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg bg-red-50 p-3">
                <p className="text-xs text-stone-500">Expense</p>
                <p className="font-mono font-semibold text-red-700">
                  {currency} {Number(dailyReport.sales_summary?.expense || 0).toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg bg-cyan-50 p-3">
                <p className="text-xs text-stone-500">Profit</p>
                <p className="font-mono font-semibold text-cyan-700">
                  {currency} {Number(dailyReport.sales_summary?.profit || 0).toFixed(2)}
                </p>
              </div>
            </div>
            {dailyReport.operations_summary && (
              <div className="grid grid-cols-2 gap-3 text-center mt-3">
                <div className="rounded-lg bg-stone-50 p-3">
                  <p className="text-xs text-stone-500">Cash transactions</p>
                  <p className="font-mono font-semibold text-stone-700">
                    {currency} {Number(dailyReport.operations_summary.cash_transactions || 0).toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg bg-stone-50 p-3">
                  <p className="text-xs text-stone-500">Credit transactions</p>
                  <p className="font-mono font-semibold text-stone-700">
                    {currency} {Number(dailyReport.operations_summary.credit_transactions || 0).toFixed(2)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Finance: Pending approvals & Due payments */}
      {(user?.role === "finance" || user?.role === "admin") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-secondary flex items-center gap-2">
                <Clock size={20} />
                Pending approvals
              </h2>
              <Link to="/invoices" className="text-sm font-medium text-primary hover:underline">View all</Link>
            </div>
            {pendingApprovals.length === 0 ? (
              <p className="text-stone-500 text-sm">No invoices pending approval.</p>
            ) : (
              <ul className="space-y-2">
                {pendingApprovals.slice(0, 10).map((inv) => (
                  <li key={inv.id}>
                    <Link
                      to={`/invoices/${inv.id}`}
                      className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-stone-50 text-stone-700"
                    >
                      <span className="font-medium">{inv.invoice_number}</span>
                      <span className="text-sm text-stone-500">{inv.currency || currency} {parseFloat(inv.total).toFixed(2)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-secondary flex items-center gap-2">
                <CreditCard size={20} />
                Due payments (next 30 days)
              </h2>
              <Link to="/invoices" className="text-sm font-medium text-primary hover:underline">View all</Link>
            </div>
            {duePayments.length === 0 ? (
              <p className="text-stone-500 text-sm">No due payments in the next 30 days.</p>
            ) : (
              <ul className="space-y-2">
                {duePayments.slice(0, 10).map((inv) => (
                  <li key={inv.id}>
                    <Link
                      to={`/invoices/${inv.id}`}
                      className="flex justify-between items-center py-2 px-3 rounded-lg hover:bg-stone-50 text-stone-700"
                    >
                      <span className="font-medium">{inv.invoice_number}</span>
                      <span className="text-sm text-amber-600">{inv.currency || currency} {Number(inv.pending).toFixed(2)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Open orders (Sales / Admin) */}
      {(user?.role === "sales" || user?.role === "admin") && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary flex items-center gap-2">
              <ShoppingCart size={20} />
              Open orders
            </h2>
            <Link
              to="/orders"
              className="text-sm font-medium text-primary hover:underline"
            >
              View all
            </Link>
          </div>
          {openOrders.length === 0 ? (
            <p className="text-stone-500 text-sm">No open orders.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600 uppercase">Customer</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-stone-600 uppercase">Amount</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-stone-600 uppercase">Payment due</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600 uppercase">Invoice</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600 uppercase">Payment</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600 uppercase">Assignee / Created by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {openOrders.slice(0, 5).map((o) => (
                    <tr key={o.id} className="hover:bg-stone-50">
                      <td className="px-3 py-2 text-sm text-stone-600">
                        {new Date(o.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <Link to={`/orders/${o.id}`} className="font-medium text-secondary hover:underline">
                          {o.customer_name || "-"}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-mono text-stone-700">
                        {o.currency || currency} {Number(o.total_amount || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-mono text-amber-700">
                        {o.currency || currency} {Number(o.payment_due || 0).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-700">
                        {o.invoice_id ? (
                          <Link to={`/invoices/${o.invoice_id}`} className="hover:underline text-primary">
                            {o.invoice_number || "View invoice"}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-700 capitalize">
                        {o.status || "open"}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-700 capitalize">
                        {(o.payment_status || "unpaid").replace("_", " ")}
                      </td>
                      <td className="px-3 py-2 text-sm text-stone-700">
                        {o.assignee_name || "-"} / {o.created_by_name || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Leads by stage (Management / Admin) — use funnel summary or stats */}
      {(user?.role === "management" || user?.role === "admin") && (funnelSummary?.by_stage || stats?.leads_by_stage) && (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-secondary mb-4 flex items-center gap-2">
            <UserPlus size={20} />
            Leads funnel
          </h2>
          {/* Order-driven funnel:
              - open -> in_progress
              - closed -> converted
              - cancelled -> lost
              There is no order-level 'new'/'assigned' concept in the DB today. */}
          <div className="flex flex-wrap gap-4">
            {[
              { key: "in_progress", label: "In progress" },
              { key: "converted", label: "Converted" },
              { key: "lost", label: "Lost" },
            ].map(({ key, label }) => {
              const byStage = funnelSummary?.by_stage || stats?.leads_by_stage || {};
              const count = byStage[key] || 0;
              const total = funnelSummary?.total ?? stats?.leads_total ?? 1;
              const pct = total ? Math.round((count / total) * 100) : 0;
              return (
                <div key={key} className="flex-1 min-w-[100px]">
                  <p className="text-sm text-stone-600 capitalize mb-1">{label}</p>
                  <p className="text-2xl font-bold text-secondary">{count}</p>
                  <div className="h-2 bg-stone-100 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts and Additional Stats (Finance / Management / Admin) */}
      {stats && (user?.role === "finance" || user?.role === "management" || user?.role === "admin") && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Collections Card */}
          <div className="lg:col-span-2 group relative overflow-hidden bg-gradient-to-br from-white to-cyan-50 rounded-2xl border-2 border-cyan-200 shadow-xl hover:shadow-2xl transition-all duration-500 animate-slideUp">
            <div
              className="absolute inset-0 opacity-5 bg-cover bg-center"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=400&fit=crop')",
              }}
            ></div>
            <div className="relative z-10 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-br from-cyan-500 to-blue-500 p-3 rounded-xl shadow-lg">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Collections Overview
                </h3>
              </div>
              <div className="space-y-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-cyan-100 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-stone-600 font-medium">
                      Total Collected
                    </span>
                    <span className="text-2xl font-bold font-mono text-emerald-600">
                      {currency} {stats.collected?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="h-3 bg-emerald-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(
                          ((stats.collected || 0) /
                            (stats.total_revenue || 1)) *
                            100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-amber-100 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-stone-600 font-medium">
                      Pending Collection
                    </span>
                    <span className="text-2xl font-bold font-mono text-amber-600">
                      {currency} {stats.pending_collection?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="h-3 bg-amber-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(
                          ((stats.pending_collection || 0) /
                            (stats.total_revenue || 1)) *
                            100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-stone-200">
                    <p className="text-xs text-stone-500 mb-1">Total Revenue</p>
                    <p className="text-lg font-bold text-gray-900">
                      {currency} {stats.total_revenue?.toFixed(2) || "0.00"}
                    </p>
                  </div>
                  <div className="bg-white/60 backdrop-blur-sm rounded-lg p-4 border border-stone-200">
                    <p className="text-xs text-stone-500 mb-1">
                      Collection Rate
                    </p>
                    <p className="text-lg font-bold text-cyan-600">
                      {(stats.total_revenue && Number(stats.total_revenue) > 0)
                        ? `${(
                            (Number(stats.collected || 0) / Number(stats.total_revenue)) *
                            100
                          ).toFixed(1)}%`
                        : "0%"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Expenses & Profit Card */}
          <div className="group relative overflow-hidden bg-gradient-to-br from-white to-teal-50 rounded-2xl border-2 border-teal-200 shadow-xl hover:shadow-2xl transition-all duration-500 animate-slideUp">
            <div
              className="absolute inset-0 opacity-5 bg-cover bg-center"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop')",
              }}
            ></div>
            <div className="relative z-10 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-br from-teal-500 to-cyan-500 p-3 rounded-xl shadow-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Financial Summary
                </h3>
              </div>
              <p className="text-xs text-stone-500 -mt-3 mb-2">
                Revenue/profit shown net of VAT for management clarity.
              </p>
              <div className="space-y-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-red-100 shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-stone-600 font-medium">
                      Total Expenses
                    </span>
                    <span className="text-xl font-bold font-mono text-red-600">
                      {currency} {stats.total_expenses?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="h-2 bg-red-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(
                          ((stats.total_expenses || 0) /
                            (stats.collected || 1)) *
                            100,
                          100
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-blue-100 shadow-md">
                  <div className="flex justify-between items-center">
                    <span className="text-stone-600 font-medium">VAT collected</span>
                    <span className="text-xl font-bold font-mono text-blue-600">
                      {currency} {Number(stats.total_vat || 0).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    Tax tracked separately from operational profit.
                  </p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 border border-stone-200 shadow-md">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-stone-600 font-medium">Vendor settlement outflow</span>
                    <span className="text-sm font-bold font-mono text-stone-700">
                      {currency} {Number(stats.vendor_settlement_cash_out || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-stone-500 text-sm">Vendor refund inflow</span>
                    <span className="text-sm font-bold font-mono text-emerald-700">
                      {currency} {Number(stats.vendor_refund_cash_in || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border-2 border-emerald-200 shadow-md">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-stone-700 font-semibold">
                      Net Profit
                    </span>
                    <span className="text-2xl font-bold font-mono text-emerald-600">
                      {currency} {stats.profit?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <PieChart className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm text-stone-600">
                      {stats.collected
                        ? `${((stats.profit / stats.collected) * 100).toFixed(
                            1
                          )}% margin`
                        : "0% margin"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
